import styled from '@emotion/styled';
import { EventTypes, VisibilityStates } from '@racemap/utilities/consts/events';
import { isDraftOrUnlistedOrArchivedOrNotActivated } from '@racemap/utilities/functions/event';
import {
  eventIsNeitherFreeNorPaid,
  isChildEvent,
  isGroupEvent,
} from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { ConfigProvider, Space, Tag } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import {
  IconContestGroup,
  IconFuture,
  IconInvisible,
  IconOk,
  IconPast,
  IconPlay,
  IconRegular,
  IconRepeat,
  IconSafemap,
  IconStageGroup,
  IconVisible,
} from '../BasicComponents/Icons';

interface BadgesProps {
  event:
    | Pick<
        Immutable<RacemapEvent>,
        'startTime' | 'endTime' | 'authorization' | 'visibility' | 'type' | 'isRepeating'
      >
    | undefined;
  withEventTypeBadge?: boolean;
  wrap?: boolean;
  /** Drops the label of the running badge, keeping it as a tooltip. */
  statusIconOnly?: boolean;
  withPaymentStateBadge?: boolean;
  /** Smaller type and tighter spacing, for badges next to a headline. */
  compact?: boolean;
}

export const EventInfoBadges: FC<BadgesProps> = ({
  event,
  withEventTypeBadge = false,
  wrap = false,
  statusIconOnly = false,
  withPaymentStateBadge = true,
  compact = false,
}) => {
  const user = useCurrentUser();
  if (user == null) return <></>;

  // A Tag has no vertical padding, so its line height is its height.
  return (
    <ConfigProvider theme={compact ? { token: { fontSizeSM: 11, lineHeightSM: 1.45 } } : undefined}>
      <Container wrap={wrap} size={compact ? 4 : 'small'}>
        {withEventTypeBadge && <EventTypeBadge event={event} />}
        <RunningBadge event={event} statusIconOnly={statusIconOnly} />
        {withPaymentStateBadge && <PaymentStateBadge event={event} />}
        <ArchivedBadge event={event} />
        <RepeatingBadge event={event} />
        {isChildEvent(event) && <GroupVisibilityBagde event={event} />}
      </Container>
    </ConfigProvider>
  );
};

const ArchivedBadge: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  if (event.visibility !== VisibilityStates.ARCHIVED) return null;

  return <Tag color="volcano">ARCHIVED</Tag>;
};

const RepeatingBadge: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  if (!event.isRepeating) return null;

  return (
    <Tag color="cyan" icon={<IconRepeat />}>
      REPEATING
    </Tag>
  );
};

const GroupVisibilityBagde: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  const isHidden = isDraftOrUnlistedOrArchivedOrNotActivated(event);

  return (
    <Tag icon={isHidden ? <IconInvisible /> : <IconVisible />} color="purple">
      {isHidden ? 'Hidden' : 'Visible'}
    </Tag>
  );
};

const EventTypeBadge: FC<BadgesProps> = ({ event }: { event: BadgesProps['event'] }) => {
  if (event == null) return null;
  const eventType = event.type;
  const groupIndex = isChildEvent(event) ? event.index : undefined;

  switch (eventType) {
    case EventTypes.REGULAR:
      return (
        <Tag icon={<IconRegular />} color="default">
          SINGLE MAP
        </Tag>
      );
    case EventTypes.CONTEST_GROUP:
      return (
        <Tag icon={<IconContestGroup />} color="default">
          GROUP MAP
        </Tag>
      );
    case EventTypes.STAGE_GROUP:
      return (
        <Tag icon={<IconStageGroup />} color="default">
          STAGES
        </Tag>
      );
    case EventTypes.SAFEMAP:
      return (
        <Tag icon={<IconSafemap />} color="default">
          SAFEMAP
        </Tag>
      );
    case EventTypes.CONTEST:
      return (
        <Tag icon={<IconContestGroup />} color="default">
          CONTEST{groupIndex != null ? ` #${groupIndex + 1}` : ''}
        </Tag>
      );
    case EventTypes.STAGE:
      return (
        <Tag icon={<IconStageGroup />} color="default">
          STAGE{groupIndex != null ? ` #${groupIndex + 1}` : ''}
        </Tag>
      );
    default:
      return null;
  }
};

export const RunningBadge: FC<BadgesProps> = ({ event, statusIconOnly = false }) => {
  if (event == null) return null;
  if (event.startTime == null || event.endTime == null) return null;
  if (event.visibility === VisibilityStates.ARCHIVED) return null;

  const start = DateTime.fromISO(event.startTime);
  const end = DateTime.fromISO(event.endTime);
  const label = (text: string) => (statusIconOnly ? null : text);

  if (DateTime.now() < start)
    return (
      <Tag icon={<IconFuture />} color="processing" variant="filled" title="UPCOMING">
        {label('UPCOMING')}
      </Tag>
    );
  if (DateTime.now() > start && DateTime.now() < end)
    return (
      <Tag icon={<IconPlay />} color="success" title="LIVE">
        {label('LIVE')}
      </Tag>
    );
  if (DateTime.now() > end)
    return (
      <Tag icon={<IconPast />} title="FINISHED">
        {label('FINISHED')}
      </Tag>
    );

  return null;
};

export const PaymentStateBadge: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  if (isGroupEvent(event)) return null;
  if (eventIsNeitherFreeNorPaid(event)) return <Tag color="warning">TRIAL</Tag>;
  if (event.visibility === VisibilityStates.ARCHIVED) return null;

  return (
    <Tag icon={<IconOk />} color="success">
      ACTIVE
    </Tag>
  );
};

const Container = styled(Space)<{ wrap?: boolean }>`
  flex-wrap: ${(props) => (props.wrap ? 'wrap' : 'nowrap')};

  svg {
    margin-right: 4px;
  }
`;
